/** A mutable flag, typically a React ref, shared by every call that must not overlap. */
export interface ExclusiveLock {
  current: boolean;
}

/**
 * Runs `task` unless another task holding `lock` is still in flight, in which
 * case the call is dropped and `false` is returned. `setBusy` is flipped for
 * the task's whole duration so a caller can mirror it into React state.
 *
 * React state alone cannot serve as the guard: it updates a tick after the
 * click, so two clicks landing before the re-render both see it unset. The
 * lock is checked and taken synchronously, which closes that window.
 */
export const runExclusive = async (
  lock: ExclusiveLock,
  setBusy: (isBusy: boolean) => void,
  task: () => Promise<void>
): Promise<boolean> => {
  if (lock.current) return false;
  lock.current = true;
  setBusy(true);
  try {
    await task();
    return true;
  } finally {
    lock.current = false;
    setBusy(false);
  }
};
